/**
 * DCMCloud — Worklist / RIS Feed Panel
 * Polls a configurable REST endpoint (or uses demo data when no endpoint is set)
 * for the radiologist's assigned study queue.
 *
 * Config: window.__dcmcloud_worklistConfig = { url, pollIntervalMs, headers }
 * Each item must conform to WorklistItem interface.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';

export interface WorklistItem {
  accessionNumber: string;
  patientName: string;
  patientId: string;
  studyDate: string;
  studyTime?: string;
  modality: string;
  studyDescription: string;
  referringPhysician?: string;
  priority: 'stat' | 'urgent' | 'routine';
  status: 'unread' | 'reading' | 'complete';
  studyInstanceUID: string;
  bodyPartExamined?: string;
}

const DEMO_ITEMS: WorklistItem[] = [
  { accessionNumber: 'ACC001', patientName: 'SMITH^JOHN', patientId: 'P100', studyDate: '20260426', modality: 'CT', studyDescription: 'CT Chest w/ Contrast', referringPhysician: 'Dr. Ahmad', priority: 'stat', status: 'unread', studyInstanceUID: '1.2.840.10008.5.1.4.1.1.2.1', bodyPartExamined: 'CHEST' },
  { accessionNumber: 'ACC002', patientName: 'JONES^MARY', patientId: 'P101', studyDate: '20260426', modality: 'MR', studyDescription: 'MRI Brain w/wo Contrast', referringPhysician: 'Dr. Patel', priority: 'urgent', status: 'unread', studyInstanceUID: '1.2.840.10008.5.1.4.1.1.4.1', bodyPartExamined: 'BRAIN' },
  { accessionNumber: 'ACC003', patientName: 'LEE^SARAH', patientId: 'P102', studyDate: '20260425', modality: 'DX', studyDescription: 'Chest X-Ray PA/Lateral', referringPhysician: 'Dr. Khan', priority: 'routine', status: 'reading', studyInstanceUID: '1.2.840.10008.5.1.4.1.1.1.1', bodyPartExamined: 'CHEST' },
  { accessionNumber: 'ACC004', patientName: 'GARCIA^LUIS', patientId: 'P103', studyDate: '20260425', modality: 'CT', studyDescription: 'CT Abdomen Pelvis', referringPhysician: 'Dr. Ahmed', priority: 'urgent', status: 'unread', studyInstanceUID: '1.2.840.10008.5.1.4.1.1.2.2', bodyPartExamined: 'ABDOMEN' },
  { accessionNumber: 'ACC005', patientName: 'WILSON^EMMA', patientId: 'P104', studyDate: '20260424', modality: 'US', studyDescription: 'Ultrasound Abdomen', referringPhysician: 'Dr. Rehman', priority: 'routine', status: 'complete', studyInstanceUID: '1.2.840.10008.5.1.4.1.1.6.1', bodyPartExamined: 'ABDOMEN' },
];

const PRIORITY_COLORS = { stat: '#ef4444', urgent: '#f59e0b', routine: '#10b981' };
const STATUS_COLORS = { unread: '#3b82f6', reading: '#f59e0b', complete: '#6b7280' };
const MODALITY_COLORS: Record<string, string> = {
  CT: '#60a5fa', MR: '#a78bfa', DX: '#34d399', US: '#fbbf24',
  PT: '#f87171', CR: '#86efac', NM: '#c084fc',
};

type SortKey = 'priority' | 'studyDate' | 'modality' | 'patientName';
const PRIORITY_ORDER = { stat: 0, urgent: 1, routine: 2 };

export function WorklistPanel() {
  const [items, setItems] = useState<WorklistItem[]>(DEMO_ITEMS);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [filterModality, setFilterModality] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('priority');
  const [search, setSearch] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchWorklist = useCallback(async () => {
    const config = (window as any).__dcmcloud_worklistConfig;
    if (!config?.url) {
      setLastRefresh(new Date());
      return; // use demo data
    }
    try {
      setLoading(true);
      const res = await fetch(config.url, { headers: config.headers ?? {} });
      const data: WorklistItem[] = await res.json();
      setItems(data);
      setLastRefresh(new Date());
    } catch (e) {
      console.warn('[DCMCloud Worklist] fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorklist();
    const interval = (window as any).__dcmcloud_worklistConfig?.pollIntervalMs ?? 60_000;
    pollRef.current = setInterval(fetchWorklist, interval);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchWorklist]);

  const openStudy = useCallback((item: WorklistItem) => {
    const baseUrl = window.location.origin + window.location.pathname;
    const url = `${baseUrl}viewer?StudyInstanceUIDs=${item.studyInstanceUID}`;
    window.location.href = url;
  }, []);

  const formatDate = (d: string) =>
    d.length === 8 ? `${d.slice(6, 8)}/${d.slice(4, 6)}/${d.slice(0, 4)}` : d;

  // Filter + sort
  let displayed = items.filter(i => {
    if (filterModality && i.modality !== filterModality) return false;
    if (filterStatus && i.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!i.patientName.toLowerCase().includes(q) &&
          !i.accessionNumber.toLowerCase().includes(q) &&
          !i.studyDescription.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  displayed = [...displayed].sort((a, b) => {
    if (sortKey === 'priority') return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
    if (sortKey === 'studyDate') return b.studyDate.localeCompare(a.studyDate);
    if (sortKey === 'modality') return a.modality.localeCompare(b.modality);
    return a.patientName.localeCompare(b.patientName);
  });

  const modalities = Array.from(new Set(items.map(i => i.modality))).sort();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#1e1e2e', color: '#e2e8f0', fontFamily: 'sans-serif', fontSize: '12px' }}>
      {/* Header */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 600, fontSize: '13px', color: '#60a5fa' }}>
          Worklist {loading && <span style={{ fontSize: '10px', color: '#6b7280' }}>↻</span>}
          <span style={{ fontSize: '10px', color: '#6b7280', marginLeft: 6 }}>({displayed.length}/{items.length})</span>
        </span>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {lastRefresh && <span style={{ fontSize: '10px', color: '#4b5563' }}>{lastRefresh.toLocaleTimeString()}</span>}
          <button onClick={fetchWorklist} title="Refresh" style={{ background: 'transparent', border: '1px solid #374151', borderRadius: 4, color: '#9ca3af', cursor: 'pointer', padding: '2px 7px', fontSize: '11px' }}>↻</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ padding: '6px 12px', borderBottom: '1px solid #1f2937', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search patient / accession…"
          style={{ flex: 1, minWidth: 100, background: '#111827', color: '#e2e8f0', border: '1px solid #374151', borderRadius: 4, padding: '4px 8px', fontSize: '12px' }}
        />
        <select value={filterModality} onChange={e => setFilterModality(e.target.value)} style={{ background: '#111827', color: '#e2e8f0', border: '1px solid #374151', borderRadius: 4, padding: '4px 6px', fontSize: '11px' }}>
          <option value="">All Modalities</option>
          {modalities.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ background: '#111827', color: '#e2e8f0', border: '1px solid #374151', borderRadius: 4, padding: '4px 6px', fontSize: '11px' }}>
          <option value="">All Status</option>
          <option value="unread">Unread</option>
          <option value="reading">Reading</option>
          <option value="complete">Complete</option>
        </select>
        <select value={sortKey} onChange={e => setSortKey(e.target.value as SortKey)} style={{ background: '#111827', color: '#e2e8f0', border: '1px solid #374151', borderRadius: 4, padding: '4px 6px', fontSize: '11px' }}>
          <option value="priority">Sort: Priority</option>
          <option value="studyDate">Sort: Date</option>
          <option value="modality">Sort: Modality</option>
          <option value="patientName">Sort: Patient</option>
        </select>
      </div>

      {/* Stat banner */}
      {displayed.some(i => i.priority === 'stat' && i.status !== 'complete') && (
        <div style={{ background: '#7f1d1d', color: '#fca5a5', padding: '4px 12px', fontSize: '11px', fontWeight: 600, borderBottom: '1px solid #ef4444' }}>
          ⚠ {displayed.filter(i => i.priority === 'stat' && i.status !== 'complete').length} STAT study(ies) pending
        </div>
      )}

      {/* Item list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {displayed.length === 0 && (
          <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>No studies match filters.</div>
        )}
        {displayed.map(item => (
          <div
            key={item.accessionNumber}
            onClick={() => openStudy(item)}
            style={{ padding: '8px 12px', borderBottom: '1px solid #1f2937', cursor: 'pointer', opacity: item.status === 'complete' ? 0.5 : 1 }}
            onMouseEnter={e => (e.currentTarget.style.background = '#111827')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: MODALITY_COLORS[item.modality] ?? '#9ca3af', background: '#111827', padding: '1px 6px', borderRadius: 4, flexShrink: 0 }}>{item.modality}</span>
              <span style={{ flex: 1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.patientName.replace('^', ', ')}</span>
              <span style={{ fontSize: '10px', color: PRIORITY_COLORS[item.priority], border: `1px solid ${PRIORITY_COLORS[item.priority]}`, borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>{item.priority.toUpperCase()}</span>
            </div>
            <div style={{ color: '#9ca3af', fontSize: '11px', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.studyDescription}</div>
            <div style={{ display: 'flex', gap: '12px', fontSize: '10px', color: '#6b7280' }}>
              <span>{formatDate(item.studyDate)}</span>
              <span>Acc: {item.accessionNumber}</span>
              {item.referringPhysician && <span>Ref: {item.referringPhysician}</span>}
              <span style={{ marginLeft: 'auto', color: STATUS_COLORS[item.status] }}>{item.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default WorklistPanel;
